import React from "react";
import { Select, DatePicker } from "antd";

export const CUSTOM = "custom";

// "No date window at all" — sent as `filterType: "ALL"` with no `dateRange`.
// Deliberately NOT part of DATE_FILTER_OPTIONS below: callers opt in with the
// `showAllTime` prop, so modules whose backend has no unbounded mode are
// unaffected.
export const ALL_TIME = "ALL";
export const ALL_TIME_OPTION = { label: "All time", value: ALL_TIME };

export const DATE_FILTER_OPTIONS = [
  { label: "Today", value: "TODAY" },
  { label: "Yesterday", value: "YESTERDAY" },
  { label: "This Week", value: "THIS_WEEK" },
  { label: "Last Week", value: "LAST_WEEK" },
  { label: "Month to date", value: "THIS_MONTH" },
  { label: "Last Month", value: "LAST_MONTH" },
  { label: "Last 30 Days", value: "LAST_30_DAYS" },
  { label: "Custom Date", value: CUSTOM },
];

/**
 * Reusable Date Range Filter Component
 * Combines a date filter dropdown with optional custom date range picker
 *
 * @param {string} dateFilter - Current selected date filter value
 * @param {Array} dateRange - [startDate, endDate] dayjs objects for custom range
 * @param {Function} onDateFilterChange - Callback when date filter dropdown changes (value) => void
 * @param {Function} onDateRangeChange - Callback when custom date range changes ([startDate, endDate]) => void
 * @param {Array} options - Custom filter options (defaults to DATE_FILTER_OPTIONS)
 * @param {boolean} showAllTime - Prepend an "All time" option (value "ALL", no
 *   date window). Off by default — opt in only where the backend supports it.
 * @param {boolean} allowClear - Show the Select's clear (x) icon (default: true)
 * @param {number} selectWidth - Width of the select dropdown (default: 150)
 * @param {string} className - Additional class names for the container
 */
const DateRangeFilter = ({
  dateFilter,
  dateRange,
  onDateFilterChange,
  onDateRangeChange,
  options = DATE_FILTER_OPTIONS,
  selectWidth = 150,
  className = "",
  label = "",
  placeholder = "Select date filter",
  disabled = false,
  // The option value that reveals the custom range picker. Defaults to the
  // built-in CUSTOM ("custom"); callers using a different vocabulary (e.g. the
  // backend "CUSTOM") pass their own.
  customValue = CUSTOM,
  showAllTime = false,
  // Whether the Select offers the clear (x) affordance. Callers that always
  // need a value selected — e.g. an "All time" default that already means
  // "no date window" — pass false so clearing isn't offered.
  allowClear = true,
}) => {
  // "All time" sits at the top when enabled. Guarded so a caller that already
  // includes it in a custom `options` list doesn't end up with it twice.
  const selectOptions = React.useMemo(() => {
    if (!showAllTime) return options;
    const hasAllTime = (options || []).some(option => option?.value === ALL_TIME);
    return hasAllTime ? options : [ALL_TIME_OPTION, ...(options || [])];
  }, [options, showAllTime]);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {label && <span className="font-normal text-black-65 text-sm">{label}:</span>}
      <Select
        value={dateFilter || undefined}
        style={{ width: selectWidth }}
        onChange={onDateFilterChange}
        options={selectOptions}
        placeholder={placeholder}
        disabled={disabled}
        allowClear={allowClear && !disabled}
      />
      {dateFilter === customValue && (
        <DatePicker.RangePicker
          value={dateRange}
          onChange={onDateRangeChange}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default DateRangeFilter;
